import { defineStore } from 'pinia';
import type { ContactTopic } from '@/services/feedback.service';

/**
 * Modale de feedback MANUELLE uniquement (formulaire email des réglages).
 * La sollicitation d'avis store est un flux séparé et silencieux : la carte
 * native est demandée directement par feedback.service (maybeRequestReview),
 * sans aucune pop-up de pré-évaluation — pattern interdit par Apple (1.1.7)
 * et Play (In-App Review API).
 *
 * `paywall_technical_issue` : le parent a refermé le paywall en déclarant un
 * bug et a accepté de raconter. Même formulaire, même envoi — seule l'entrée
 * change, et le message part pré-rempli du contexte technique.
 */
export type EntrySource =
  | 'settings'
  | 'paywall_technical_issue'
  | 'settings_contact'
  | 'community_guidelines';

/**
 * `contact` (HARMONIA-54) : « Nous contacter », qui ouvrait un formulaire
 * Teamwork dans un navigateur. Même feuille, même envoi, sans les notes.
 */
export type FeedbackMode = 'feedback' | 'contact';

export interface FeedbackOpenOptions {
  /**
   * Texte déposé dans le champ message à l'ouverture. Le parent écrit à la
   * suite : on lui donne le contexte qu'il ne peut pas connaître (écran,
   * version, plateforme), pas les mots qu'il a à dire.
   */
  prefillMessage?: string;
  mode?: FeedbackMode;
  /** Sujet présélectionné (mode contact). */
  topic?: ContactTopic;
}

export const useFeedbackStore = defineStore('feedback', {
  state: () => ({
    isOpen: false,
    source: null as EntrySource | null,
    prefillMessage: null as string | null,
    mode: 'feedback' as FeedbackMode,
    topic: null as ContactTopic | null,
  }),
  actions: {
    open(source: EntrySource, options: FeedbackOpenOptions = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.source = source;
      this.prefillMessage = options.prefillMessage ?? null;
      this.mode = options.mode ?? 'feedback';
      this.topic = options.topic ?? null;
    },
    close() {
      this.isOpen = false;
      this.source = null;
      this.prefillMessage = null;
      this.topic = null;
      // `mode` reste tel quel : la feuille se referme en animation, elle ne
      // doit pas basculer sur l'autre formulaire pendant qu'elle descend.
    },
  },
});
